/**
 * WeatherSystem.js
 *
 * June 2026 — Dynamic, makeup-driven weather for planet/moon surfaces.
 *
 * Replaces the old static "biome haze" with a living weather state machine that
 * picks weather appropriate to a body's makeup (type + atmosphere + habitability)
 * and ebbs/flows over time:  calm → building → peak storm → clearing.
 *
 * Drives:
 *   - A single reconfigurable CPU ParticleSystem (rain / snow / dust / ash /
 *     embers / acid) that follows the player.
 *   - Fog density + colour modulation (peak storms can cut visibility — dramatic).
 *   - Sun / hemi dimming during storms.
 *   - Lightning: screen-flash (via onLightning), an in-scene ambient flash, and
 *     a thunder rumble (via onLightning → AudioSystem).
 *   - A HUD weather readout (via onState).
 *
 * Self-contained Babylon; ticked by GameScene AFTER the day/night pass so it can
 * layer on top of the day/night lighting. Suspended in space / caves / interiors.
 */

import {
  Vector3,
  Color3,
  Color4,
  DynamicTexture,
  ParticleSystem,
  TransformNode,
} from '@babylonjs/core';

// ---- Weather definitions ---------------------------------------------------
// particle: preset name (see _configureParticles) or null for fog-only weather.
// fogBoost: extra fog density at full intensity (× base). darken: light cut at
// full intensity. lightning: {enabled, color}. tint: fog colour at full intensity.
const WEATHER = {
  clear: { label: 'Clear', icon: '☀', particle: null, fogBoost: 0, darken: 0, tint: null },
  mist: { label: 'Mist', icon: '🌫', particle: null, fogBoost: 1.8, darken: 0.12, tint: [0.62, 0.66, 0.72] },
  rain: { label: 'Rain', icon: '🌧', particle: 'rain', emitMax: 900, fogBoost: 1.4, darken: 0.4, tint: [0.42, 0.46, 0.52] },
  thunderstorm: {
    label: 'Thunderstorm', icon: '⛈', particle: 'rain', emitMax: 1300, fogBoost: 2.2, darken: 0.65,
    tint: [0.3, 0.33, 0.4], lightning: { enabled: true, color: [0.85, 0.9, 1.0] },
  },
  snow: { label: 'Snowfall', icon: '🌨', particle: 'snow', emitMax: 520, fogBoost: 1.4, darken: 0.22, tint: [0.82, 0.86, 0.92] },
  blizzard: { label: 'Blizzard', icon: '❄', particle: 'snow', emitMax: 1100, fogBoost: 3.4, darken: 0.5, tint: [0.9, 0.93, 0.98] },
  dust: { label: 'Dust', icon: '🌬', particle: 'dust', emitMax: 600, fogBoost: 1.6, darken: 0.25, tint: [0.7, 0.56, 0.34] },
  sandstorm: { label: 'Sandstorm', icon: '🏜', particle: 'dust', emitMax: 1300, fogBoost: 3.6, darken: 0.55, tint: [0.74, 0.55, 0.3] },
  ashfall: { label: 'Ashfall', icon: '🌋', particle: 'ash', emitMax: 560, fogBoost: 1.6, darken: 0.32, tint: [0.28, 0.24, 0.22] },
  ember_storm: {
    label: 'Ember Storm', icon: '🔥', particle: 'ember', emitMax: 900, fogBoost: 1.8, darken: 0.28,
    tint: [0.4, 0.16, 0.08], lightning: { enabled: true, color: [1.0, 0.55, 0.25] },
  },
  acid_rain: { label: 'Acid Rain', icon: '🧪', particle: 'acid', emitMax: 850, fogBoost: 1.6, darken: 0.42, tint: [0.3, 0.42, 0.16] },
  toxic_storm: {
    label: 'Toxic Storm', icon: '☣', particle: 'acid', emitMax: 1200, fogBoost: 2.6, darken: 0.6,
    tint: [0.22, 0.36, 0.1], lightning: { enabled: true, color: [0.55, 1.0, 0.4] },
  },
  toxic_fog: { label: 'Toxic Fog', icon: '🌫', particle: null, fogBoost: 3.0, darken: 0.3, tint: [0.26, 0.4, 0.14] },
};

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function smoothBell(x) {
  // 0 at ends, 1 in the middle third; smoothstep on the ramps.
  let r;
  if (x < 0.28) r = x / 0.28;
  else if (x > 0.72) r = (1 - x) / 0.28;
  else r = 1;
  r = Math.max(0, Math.min(1, r));
  return r * r * (3 - 2 * r);
}

export default class WeatherSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.sun = opts.sun || null;       // directional light
    this.hemi = opts.hemi || null;     // hemispheric light
    this.onLightning = opts.onLightning || (() => {});
    this.onState = opts.onState || (() => {});

    this._disposed = false;
    this._enabled = false;
    this._tex = null;
    this._ps = null;
    this._anchor = null;
    this._curKind = '__none__';

    // Base environment captured from GameScene so we can modulate around it.
    this._baseFogDensity = opts.baseFogDensity ?? 0.011;
    this._baseFogColor = opts.baseFogColor ? opts.baseFogColor.clone() : new Color3(0.1, 0.1, 0.15);

    // State machine.
    this._profile = { calm: ['clear'], active: [] };
    this._rng = mulberry32(1);
    this._cur = 'clear';
    this._intensity = 0;
    this._segT = 0;
    this._segDur = 24;
    this._peak = 0;
    this._inActiveSeg = false;

    // Lightning.
    this._flash = 0;
    this._strikeTimer = 3;

    // Throttled HUD publishing.
    this._lastPublishedKey = '';

    this._buildTexture();
    this._buildParticleSystem();
  }

  _buildTexture() {
    const size = 64;
    const dt = new DynamicTexture('weatherParticleTex', size, this.scene, false);
    const ctx = dt.getContext();
    const g = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.7)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    dt.hasAlpha = true;
    dt.update();
    this._tex = dt;
  }

  _buildParticleSystem() {
    const anchor = new TransformNode('weatherAnchor', this.scene);
    this._anchor = anchor;
    const ps = new ParticleSystem('weatherParticles', 1600, this.scene);
    ps.particleTexture = this._tex;
    ps.emitter = anchor;
    ps.emitRate = 0;
    ps.updateSpeed = 0.02;
    ps.gravity = new Vector3(0, -9, 0);
    ps.minEmitBox = new Vector3(-55, 28, -55);
    ps.maxEmitBox = new Vector3(55, 42, 55);
    ps.isLocal = false;
    ps.start();
    this._ps = ps;
  }

  // --- public API ----------------------------------------------------------

  setBody(body, seed = 1) {
    this._profile = this._deriveProfile(body);
    this._rng = mulberry32(((seed | 0) >>> 0) || 7);
    // Start calm; first segment kicks in via tick.
    this._cur = this._profile.calm[0] || 'clear';
    this._intensity = 0;
    this._segT = 0;
    this._segDur = 16 + this._rng() * 14;
    this._peak = 0;
    this._inActiveSeg = false;
    this._applyKind(this._cur);
    this._publish(true);
  }

  setEnabled(on) {
    this._enabled = !!on;
    if (!on && this._ps) this._ps.emitRate = 0;
    if (!on) {
      // Hand fog back to its base so caves/space/interiors aren't left foggy.
      this._flash = 0;
    }
  }

  _deriveProfile(body) {
    const type = body?.kind === 'moon' ? 'Moon' : (body?.type || 'Default');
    const a = String(body?.atmosphere || 'None').toLowerCase();
    const hab = Number(body?.habitability ?? 0) || 0;
    const atm = a.includes('toxic') ? 'toxic'
      : (a === '' || a === 'none' || a === 'unknown') ? 'none'
        : a.includes('thin') ? 'thin'
          : a.includes('dense') ? 'dense' : 'normal';

    // Airless bodies have no weather (physically correct — a vacuum).
    if (atm === 'none') return { calm: ['clear'], active: [] };

    if (type === 'Lava') return { calm: ['clear', 'ashfall'], active: ['ashfall', 'ember_storm'] };
    if (type === 'Ice Giant') return { calm: ['clear', 'snow'], active: ['snow', 'blizzard'] };
    if (type === 'Ocean') return { calm: ['mist', 'clear'], active: ['rain', 'thunderstorm'] };

    if (atm === 'toxic') return { calm: ['toxic_fog', 'clear'], active: ['acid_rain', 'toxic_storm'] };

    // Arid terrestrial / dwarf → dust & sandstorms.
    if ((type === 'Terrestrial' || type === 'Dwarf') && hab < 40) {
      return { calm: ['clear', 'dust'], active: ['dust', 'sandstorm'] };
    }
    // Temperate, breathable, habitable → rain & thunderstorms.
    if (hab >= 40) return { calm: ['clear', 'mist'], active: ['rain', 'thunderstorm'] };

    // Generic fallbacks by atmosphere thickness.
    if (atm === 'dense') return { calm: ['mist', 'clear'], active: ['rain', 'thunderstorm'] };
    return { calm: ['clear', 'dust'], active: ['dust'] };
  }

  _pick(list) {
    if (!list || list.length === 0) return 'clear';
    return list[Math.floor(this._rng() * list.length) % list.length];
  }

  _nextSegment() {
    this._inActiveSeg = !this._inActiveSeg && this._profile.active.length > 0;
    this._segT = 0;
    if (this._inActiveSeg) {
      this._cur = this._pick(this._profile.active);
      this._peak = 0.55 + this._rng() * 0.45;       // 0.55 .. 1.0
      this._segDur = 28 + this._rng() * 46;          // 28 .. 74s
    } else {
      this._cur = this._pick(this._profile.calm);
      const def = WEATHER[this._cur];
      this._peak = def && def.particle ? 0.18 + this._rng() * 0.2 : 0; // calm light or 0
      this._segDur = 16 + this._rng() * 26;          // 16 .. 42s
    }
    this._applyKind(this._cur);
    // New per-segment wind bias (slants rain, drifts dust/snow).
    this._wind = new Vector3((this._rng() - 0.5) * 2, 0, (this._rng() - 0.5) * 2);
  }

  _applyKind(weatherId) {
    const def = WEATHER[weatherId] || WEATHER.clear;
    const kind = def.particle || 'none';
    if (kind === this._curKind) return;
    this._curKind = kind;
    this._configureParticles(kind);
  }

  _configureParticles(kind) {
    const ps = this._ps;
    if (!ps) return;
    ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    ps.minScaleX = 1; ps.maxScaleX = 1;
    ps.minScaleY = 1; ps.maxScaleY = 1;
    switch (kind) {
      case 'rain':
      case 'acid': {
        const acid = kind === 'acid';
        ps.color1 = acid ? new Color4(0.5, 0.85, 0.3, 0.55) : new Color4(0.6, 0.7, 0.85, 0.45);
        ps.color2 = acid ? new Color4(0.35, 0.6, 0.2, 0.5) : new Color4(0.5, 0.6, 0.75, 0.4);
        ps.colorDead = new Color4(0.4, 0.5, 0.6, 0);
        ps.minSize = 0.12; ps.maxSize = 0.22;
        ps.minScaleY = 5; ps.maxScaleY = 9;        // stretched into streaks
        ps.minScaleX = 0.25; ps.maxScaleX = 0.4;
        ps.minLifeTime = 0.7; ps.maxLifeTime = 1.2;
        ps.gravity = new Vector3(0, -55, 0);
        ps.direction1 = new Vector3(-0.5, -1, -0.5);
        ps.direction2 = new Vector3(0.5, -1, 0.5);
        ps.minEmitPower = 10; ps.maxEmitPower = 18;
        ps.minEmitBox = new Vector3(-50, 26, -50);
        ps.maxEmitBox = new Vector3(50, 40, 50);
        ps.updateSpeed = 0.03;
        break;
      }
      case 'snow':
        ps.color1 = new Color4(1, 1, 1, 0.95);
        ps.color2 = new Color4(0.85, 0.9, 1, 0.85);
        ps.colorDead = new Color4(0.8, 0.85, 0.95, 0);
        ps.minSize = 0.12; ps.maxSize = 0.32;
        ps.minLifeTime = 4; ps.maxLifeTime = 8;
        ps.gravity = new Vector3(0, -2.2, 0);
        ps.direction1 = new Vector3(-0.4, -0.3, -0.4);
        ps.direction2 = new Vector3(0.4, -0.6, 0.4);
        ps.minEmitPower = 0.4; ps.maxEmitPower = 1.2;
        ps.minEmitBox = new Vector3(-50, 22, -50);
        ps.maxEmitBox = new Vector3(50, 38, 50);
        ps.updateSpeed = 0.02;
        break;
      case 'dust':
        ps.color1 = new Color4(0.85, 0.72, 0.45, 0.5);
        ps.color2 = new Color4(0.65, 0.5, 0.28, 0.42);
        ps.colorDead = new Color4(0.4, 0.3, 0.15, 0);
        ps.minSize = 0.2; ps.maxSize = 0.7;
        ps.minLifeTime = 1.8; ps.maxLifeTime = 4;
        ps.gravity = new Vector3(0, -0.3, 0);
        ps.direction1 = new Vector3(0.6, -0.1, 0.4);
        ps.direction2 = new Vector3(1.6, 0.2, 1.2);
        ps.minEmitPower = 6; ps.maxEmitPower = 16;
        ps.minEmitBox = new Vector3(-55, 1, -55);
        ps.maxEmitBox = new Vector3(55, 18, 55);
        ps.updateSpeed = 0.025;
        break;
      case 'ash':
        ps.color1 = new Color4(0.4, 0.38, 0.36, 0.7);
        ps.color2 = new Color4(0.22, 0.2, 0.2, 0.6);
        ps.colorDead = new Color4(0.1, 0.1, 0.1, 0);
        ps.minSize = 0.12; ps.maxSize = 0.4;
        ps.minLifeTime = 3; ps.maxLifeTime = 6;
        ps.gravity = new Vector3(0, -3, 0);
        ps.direction1 = new Vector3(-0.4, -0.4, -0.4);
        ps.direction2 = new Vector3(0.4, -0.7, 0.4);
        ps.minEmitPower = 0.5; ps.maxEmitPower = 1.6;
        ps.minEmitBox = new Vector3(-52, 22, -52);
        ps.maxEmitBox = new Vector3(52, 40, 52);
        ps.updateSpeed = 0.02;
        break;
      case 'ember':
        ps.blendMode = ParticleSystem.BLENDMODE_ADD;
        ps.color1 = new Color4(1.0, 0.6, 0.2, 0.9);
        ps.color2 = new Color4(1.0, 0.35, 0.1, 0.8);
        ps.colorDead = new Color4(0.5, 0.08, 0.0, 0);
        ps.minSize = 0.08; ps.maxSize = 0.26;
        ps.minLifeTime = 1.6; ps.maxLifeTime = 3.4;
        ps.gravity = new Vector3(0, -4, 0);
        ps.direction1 = new Vector3(-0.5, -0.6, -0.5);
        ps.direction2 = new Vector3(0.5, -1.0, 0.5);
        ps.minEmitPower = 1; ps.maxEmitPower = 4;
        ps.minEmitBox = new Vector3(-50, 24, -50);
        ps.maxEmitBox = new Vector3(50, 42, 50);
        ps.updateSpeed = 0.025;
        break;
      default: // none
        ps.emitRate = 0;
        break;
    }
  }

  // --- per-frame -------------------------------------------------------------

  tick(dt) {
    if (this._disposed || !this._enabled) return;
    const def = WEATHER[this._cur] || WEATHER.clear;

    // 1) Advance the weather state machine.
    this._segT += dt;
    if (this._segT >= this._segDur) this._nextSegment();
    const env = smoothBell(this._segT / this._segDur);
    this._intensity = this._peak * env;
    const I = this._intensity;

    // 2) Follow the player + drive particle emission.
    const p = this.getPlayerPos();
    if (p && this._anchor) this._anchor.position.set(p.x, p.y, p.z);
    if (this._ps) {
      if (def.particle && I > 0.02) {
        this._ps.emitRate = (def.emitMax || 600) * I;
        // Apply per-segment wind to the horizontal drift.
        if (this._wind) {
          const w = this._wind;
          const d1 = this._ps.direction1;
          const d2 = this._ps.direction2;
          d1.x += (w.x - d1.x) * 0.04;
          d1.z += (w.z - d1.z) * 0.04;
          d2.x += (w.x - d2.x) * 0.04;
          d2.z += (w.z - d2.z) * 0.04;
        }
      } else {
        this._ps.emitRate = 0;
      }
    }

    // 3) Fog: thicken + tint toward the weather colour. Peak storms cut sight.
    const fogMult = 1 + (def.fogBoost || 0) * I;
    let targetDensity = this._baseFogDensity * fogMult;
    targetDensity = Math.min(targetDensity, 0.09); // hard ceiling so it's never opaque
    this.scene.fogDensity = targetDensity;
    if (def.tint && this.scene.fogColor) {
      const t = Math.min(1, I * 0.9);
      const fc = this.scene.fogColor;
      const base = this._baseFogColor;
      fc.r = base.r + (def.tint[0] - base.r) * t;
      fc.g = base.g + (def.tint[1] - base.g) * t;
      fc.b = base.b + (def.tint[2] - base.b) * t;
    } else if (this.scene.fogColor) {
      const fc = this.scene.fogColor;
      fc.r = this._baseFogColor.r; fc.g = this._baseFogColor.g; fc.b = this._baseFogColor.b;
    }

    // 4) Lightning timing for electrical weathers.
    if (def.lightning?.enabled && I > 0.32) {
      this._strikeTimer -= dt;
      if (this._strikeTimer <= 0) {
        this._strike(def.lightning, I);
        // More frequent strikes at higher intensity (1.2s heavy → 7s light).
        this._strikeTimer = 1.2 + (1 - I) * 6 + this._rng() * 1.5;
      }
    }

    // 5) Storm darkening + lightning flash applied AFTER day/night.
    this._flash = Math.max(0, this._flash - dt * 3.2);
    const darkenMul = 1 - (def.darken || 0) * I * 0.9;
    if (this.sun) this.sun.intensity *= darkenMul;
    if (this.hemi) {
      this.hemi.intensity *= darkenMul;
      this.hemi.intensity += this._flash * 1.4; // bright sky flash on a strike
    }
    if (this._flash > 0.001 && this.scene.fogColor) {
      const f = this._flash;
      const fc = this.scene.fogColor;
      fc.r = Math.min(1, fc.r + f * 0.6);
      fc.g = Math.min(1, fc.g + f * 0.6);
      fc.b = Math.min(1, fc.b + f * 0.7);
    }

    this._publish(false);
  }

  _strike(lightning, intensity) {
    this._flash = 0.9;
    try { this.onLightning(intensity, lightning.color); } catch (_e) { /* noop */ }
  }

  _severity(I) {
    if (I < 0.06) return 'calm';
    if (I < 0.35) return 'light';
    if (I < 0.7) return 'moderate';
    return 'heavy';
  }

  _publish(force) {
    const def = WEATHER[this._cur] || WEATHER.clear;
    const sev = def.particle || def.fogBoost > 0 ? this._severity(this._intensity) : 'calm';
    const key = `${this._cur}|${sev}`;
    if (!force && key === this._lastPublishedKey) return;
    this._lastPublishedKey = key;
    try {
      this.onState({
        id: this._cur,
        label: def.label,
        icon: def.icon,
        intensity: Math.round(this._intensity * 100) / 100,
        severity: sev,
      });
    } catch (_e) { /* noop */ }
  }

  dispose() {
    this._disposed = true;
    try { this._ps?.dispose(); } catch (_e) { /* noop */ }
    try { this._anchor?.dispose(); } catch (_e) { /* noop */ }
    try { this._tex?.dispose(); } catch (_e) { /* noop */ }
    this._ps = null; this._anchor = null; this._tex = null;
  }
}
